import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// mantine
import { Loader, ScrollArea, Switch, TextInput } from '@mantine/core';
import { useDebouncedState, useDisclosure } from '@mantine/hooks';
// components
import { Search } from 'lucide-react';
import type { TAttribute, TAttributeData } from '@newo/api/gateway';
import ContextMenu from '@/pages/Attributes/Customer/components/ContextMenu';
import GroupTitle from '@/pages/Attributes/components/GroupTitle';
import Attribute from '@/pages/Attributes/components/Attribute';
import AskVibeModeSearchCard from '@/modules/VibeMode/components/AskVibeModeSearchCard';
import Drawer from '@/components/Drawer';
import GroupNavigation from '@/pages/Attributes/components/GroupNavigation';
import Navigation from '@/pages/Attributes/components/Navigation';
import AttributeForm from '@/pages/Attributes/components/AttributeForm';
import NoRecords from '@/components/NoRecords';
// icons
// utils
import { PATHS } from '@/const';
import {
    useCustomerCreateAttributeMutation,
    useCustomerDeleteAttributeMutation,
    useCustomerUpdateAttributeMutation
} from '@/services/customer/customer/customer.queries.ts';
import { useCustomerAttributesQuery } from '@/services/bff/bff.queries.ts';
import { openConfirmDeleteModal } from '@/utils/ui.tsx';
import { PageContainer } from '@/components/PageContainer/PageContainer.tsx';
import { sortByNumericPrefix, sortByNumericPrefixProperty } from '@/utils';
import { useAppStore } from '@/store/app';

const DEBOUNCE_TIMEOUT = 200;

const Customer = () => {
    const viewport = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const store = useAppStore();
    const [isOpened, handlers] = useDisclosure();
    const [selectedAttribute, setSelectedAttribute] = useState<
        TAttribute | undefined
    >();
    const { group: groupParameter } = useParams();
    const group = groupParameter
        ? decodeURIComponent(groupParameter)
        : undefined;
    const [searchTerm, setSearchTerm] = useDebouncedState('', DEBOUNCE_TIMEOUT);

    const { data, refetch, isLoading } = useCustomerAttributesQuery({
        query: searchTerm || undefined,
        include_hidden: store.isAttrsHiddenShown
    });
    const { mutateAsync: createAttribute } =
        useCustomerCreateAttributeMutation();
    const { mutate: deleteAttribute } = useCustomerDeleteAttributeMutation();
    const { mutateAsync: updateAttribute } =
        useCustomerUpdateAttributeMutation();

    useEffect(() => {
        if (searchTerm) {
            navigate(`${PATHS.attributes.root}/customer`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]); // exclude navigate to prevent useEffect trigger

    useEffect(() => {
        if (!searchTerm && !group && data?.groups.length) {
            const [first] = data.groups;
            navigate(
                `${PATHS.attributes.root}/customer/${encodeURIComponent(first)}`
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, group, data?.groups]); // exclude navigate to prevent useEffect trigger

    useEffect(() => {
        viewport.current?.scrollTo({ top: 0 });
    }, [group]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.currentTarget.value);
    };

    const handleOpen = () => {
        handlers.open();
    };

    const handleClose = () => {
        handlers.close();
        setSelectedAttribute(undefined);
    };

    const handleAttributeSubmit = async ({
        attributeId,
        data: formData
    }: {
        attributeId?: string;
        data: TAttributeData;
    }) => {
        await (attributeId
            ? updateAttribute({ attributeId, data: formData })
            : createAttribute(formData));
        await refetch();
        handleClose();
    };

    const handleEdit = (attribute: TAttribute) => {
        handleOpen();
        setSelectedAttribute(attribute);
    };

    const handleDelete = (attribute: TAttribute) => {
        openConfirmDeleteModal({
            title: `Delete ${attribute.idn} attribute?`,
            children: `Are you sure you want to delete ${attribute.idn} attribute?`,
            confirmLabel: 'Delete Attribute',
            onConfirm: () => {
                deleteAttribute(
                    { attributeId: attribute.id },
                    {
                        onSuccess: () => {
                            refetch();
                        }
                    }
                );
            }
        });
    };

    const groups = sortByNumericPrefix(data?.groups ?? []);

    const items = useMemo(() => {
        const attributes = data?.attributes ?? [];
        let filteredAttributes: TAttribute[];

        if (group === 'Unassigned') {
            filteredAttributes = attributes.filter(
                (attribute) =>
                    attribute.group === 'unassigned' || !attribute.group
            );
        } else if (group) {
            filteredAttributes = attributes.filter(
                (attribute) => attribute.group === group
            );
        } else {
            filteredAttributes = attributes;
        }

        return sortByNumericPrefixProperty(filteredAttributes, 'title');
    }, [data?.attributes, group]);

    // TODO: Move unassigned to BE
    const unassignedItems = data?.attributes.filter(
        (attribute) => attribute.group === 'unassigned' || !attribute.group
    );

    return (
        <>
            <PageContainer className="pt-6 !grid !gap-4 !h-full !overflow-hidden !grid-cols-[300px_1fr]">
                <div className="flex flex-col gap-4 overflow-hidden">
                    <Navigation className="mb-1" value="customer" />
                    <ScrollArea h="100%" scrollbars="y" type="hover">
                        <GroupNavigation
                            baseUrl={`${PATHS.attributes.root}/customer`}
                            groups={groups}
                            selectedGroup={group}
                            withAll={!!searchTerm}
                            withUnassigned={!!unassignedItems?.length}
                        />
                    </ScrollArea>
                </div>
                <div className="overflow-hidden pb-8 flex-1 flex flex-col">
                    <div className="flex items-center gap-8 pb-4">
                        <TextInput
                            className="w-1/2"
                            defaultValue={searchTerm}
                            leftSection={<Search />}
                            placeholder="Search by idn, title, group"
                            size="sm"
                            onChange={handleSearchChange}
                        />
                        <Switch
                            checked={store.isAttrsHiddenShown}
                            className="ml-auto"
                            label="Show Hidden"
                            onChange={() => {
                                store.toggleAttrsHiddenShown();
                            }}
                        />
                        <ContextMenu refetch={refetch} onAdd={handleOpen} />
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center py-60">
                            <Loader />
                        </div>
                    ) : (
                        <ScrollArea
                            h="100%"
                            scrollbars="y"
                            type="hover"
                            viewportRef={viewport}
                        >
                            <div className="flex flex-col gap-4 pr-4">
                                {searchTerm ? (
                                    <AskVibeModeSearchCard query={searchTerm} />
                                ) : undefined}
                                <GroupTitle>{group ?? 'all'}</GroupTitle>
                                {items.length === 0 ? <NoRecords /> : undefined}
                                {items.map((item) => (
                                    <Attribute
                                        withVibeConsultation
                                        attribute={item}
                                        key={item.id}
                                        onDelete={handleDelete}
                                        onEdit={handleEdit}
                                        onSave={handleAttributeSubmit}
                                    />
                                ))}
                            </div>
                        </ScrollArea>
                    )}
                </div>
            </PageContainer>
            <Drawer
                withOverlay
                name="customer-attributes"
                opened={isOpened}
                position="right"
                size="md"
                onClose={handleClose}
            >
                <AttributeForm
                    attribute={selectedAttribute}
                    groups={groups}
                    onClose={handleClose}
                    onSubmit={handleAttributeSubmit}
                />
            </Drawer>
        </>
    );
};

export default Customer;
